/**
 * The background-music playback shell (ADR-0146): owns the in-browser General-MIDI
 * synth (SpessaSynth, Apache-2.0) and streams the baked UO `.mid` login playlist
 * through it. The music sibling of {@link ./audioManager.AudioManager} - deliberately
 * thin and free of sequencing *decisions*, which live in the pure, unit-tested
 * {@link ./musicDirector.MusicDirector} (over {@link ./playlist.Playlist}); this layer
 * is the small imperative wrapper around the Web Audio + AudioWorklet API (which jsdom
 * lacks, so it is largely not unit tested, like `audioManager`).
 *
 * **Duel-combat override** (ADR-0146): while the *local* player is in a live duel,
 * {@link MusicPlayer.setCombat} swaps the mix to the looping `combat1` track and reverts
 * to the login playlist when the duel ends (any path). The director owns that decision;
 * this shell just (re)loads its one sequencer song. Because it is keyed on the player's
 * own replicated combat stance (driven from `main.ts`), both duelists switch and every
 * other client's music is untouched - no networked music state. If `combat1` isn't baked
 * it degrades to leaving the playlist playing. Every track swap (duel-in, duel-out, and
 * login changes) fades the incoming track in from silence (see {@link FADE_SECONDS}).
 *
 * **Storm override** (PRD #289 storm fix): {@link MusicPlayer.setStorm} is the twin, driven
 * off the replicated StormStatus active edge, swapping to the looping `dungeon2` dirge for a
 * Blackthorn's Storm's whole duration. It **outranks** the duel override (a storm cancels
 * every duel, so they never overlap), degrades the same way when `dungeon2` isn't baked, and
 * reverts to the login playlist (or a duel) when the storm ends.
 *
 * **Arena intensity remixes**: beside the synth there is a second playback lane - the
 * Arena battle tracks' escalating `.mp3` remixes (`<id>_2..5`, pre-rendered audio) play
 * as decoded {@link AudioBuffer}s through an {@link AudioBufferSourceNode} into the SAME
 * music gain, so the slider/mute/fade rules apply identically. Exactly one lane sounds
 * at a time: loading a remix pauses the sequencer, loading a mid stops the source, and a
 * finished source drives the same songEnded advance as the synth. Which id (mid or
 * remix) plays is entirely the director's call ({@link MusicDirector.setArenaTier}) -
 * this shell only routes by "is the id a decoded remix". Decoded remix residency is
 * **bounded** (issue #626): at most the active run song's family plus a small warm
 * set of recent families stays decoded ({@link ./remixResidency}); older families
 * are released - deferred while the live source still plays one - and a released
 * family simply re-fetches on a later run. The MIDI tracks + the shared soundfont
 * stay resident (their policy is unchanged - they are small next to the mp3 lane).
 *
 * The synth output feeds a **dedicated music {@link GainNode}** on the **shared**
 * {@link AudioContext} (borrowed from `AudioManager` so the whole app has one context),
 * so the music volume/mute slider (settings panel, `src/music.ts`) rides only this gain
 * and never the SFX master. One track is loaded into the sequencer at a time with
 * per-song looping off (`loopCount = 0`); when it finishes, the synth's `songEnded`
 * event advances the pure playlist (wrapping past the last track back to the first) and
 * plays whatever it lands on - so the login playlist repeats forever.
 *
 * Graceful absence, mirroring the sound path: with no soundfont, no baked `.mid`, or a
 * synth/worklet failure, every step degrades to a quiet no-op rather than throwing -
 * music is a nicety, never load-bearing. `start()` and `init()` are idempotent, so a
 * reconnect's second `Welcome` neither re-inits the synth nor restarts the playlist
 * (no stacked players, no leaked nodes); the single context is never recreated.
 *
 * **Mute semantics** (decided, ADR-0146): mute pins the music gain to 0 (hard silence)
 * while the sequencer keeps running underneath, so unmuting resumes exactly where the
 * music is - identical to how the SFX master mute works. Both the volume and the mute
 * persist in localStorage (`src/music.ts`) and are applied live.
 */
import { WorkletSynthesizer, Sequencer } from 'spessasynth_lib';
// The AudioWorklet processor SpessaSynth runs the synth in. Static `?url` import so
// Vite hashes it and serves it same-origin for `audioWorklet.addModule` (it must be
// fetched by URL, not bundled into the main graph).
import processorUrl from 'spessasynth_lib/dist/spessasynth_processor.min.js?url';
import { MusicDirector, VICTORY_TRACK_ID } from './musicDirector';
import { loadMusicTracks, loadMusicRemixes, loadSoundfont } from './musicAssets';
import { RemixFamilyResidency, remixFamilyOf } from './remixResidency';
import {
  LOGIN_PLAYLIST,
  ARENA_PLAYLIST,
  ARENA_INTENSITY_TIERS,
  arenaRemixTrackId,
} from '../sim/content';

/**
 * The loudness ceiling the music mix sits under, so a full-volume playlist stays
 * politely beneath speech, UI, and the SFX soundscape. The user's music slider rides
 * *within* this ceiling: effective music gain = `level * MUSIC_CEILING`, so full level
 * (1) is this value and the player can only turn it down from there.
 */
const MUSIC_CEILING = 0.5;

/**
 * The fade-in ramp (seconds) applied to the incoming track on **every** track swap - the
 * duel-in and duel-out combat swaps *and* the ordinary login-playlist track changes
 * (ADR-0146). A true crossfade isn't possible here: the single synth + single sequencer
 * loads exactly one track at a time, so there is nothing to fade the *outgoing* track
 * against, and a timer-based fade-out-then-swap would risk desync under rapid duel
 * toggles. Instead the incoming track ramps up from silence (a hard-cut outgoing) - a
 * timer-free, last-write-wins transition that stays robust under rapid toggles and always
 * honours mute/volume (the ramp target is the current {@link MusicPlayer.musicGain}, and a
 * mid-fade slider/mute cancels the ramp - see {@link MusicPlayer.applyGain}).
 */
const FADE_SECONDS = 0.6;

/** Clamp a number into `[0, 1]`. */
function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

export class MusicPlayer {
  private ctx?: AudioContext;
  private gain?: GainNode;
  private synth?: WorkletSynthesizer;
  private seq?: Sequencer;
  private readonly tracks = new Map<string, ArrayBuffer>();
  // The decoded Arena intensity remixes (mp3 lane), keyed by remix id (`combat1_3`).
  private readonly remixes = new Map<string, AudioBuffer>();
  // Per-song in-flight/settled remix loads, keyed by base arena song id, so a re-entered
  // run (or a repeated setArena edge) never re-downloads a song's remixes. The whole mp3
  // lane is deferred to arena entry - the ~85MB of remixes is never fetched or decoded
  // outside a run, and even then only the one chosen run song's `_2.._5` are loaded.
  private readonly remixLoads = new Map<string, Promise<void>>();
  // The current run's remix load, exposed via whenArenaRemixesReady() so tests drive the
  // ramp deterministically; main.ts fires setArena and never awaits it (the base mid plays
  // while it loads, and the ramp only reaches a remix at a track boundary ~100s+ in).
  private arenaRemixLoad?: Promise<void>;
  // The bounded remix-family residency (issue #626): at most the active run song's
  // family + a small warm set stay decoded; older families are released once no
  // live playback references them (see pruneRemixFamilies).
  private remixFamilies = new RemixFamilyResidency();
  // The one live remix source, when the mp3 lane owns the mix. Replaced/stopped on
  // every track swap; its onended is detached first so a swap never double-advances.
  private source?: AudioBufferSourceNode;
  // The remix family the live source is playing (undefined when the synth lane owns
  // the mix), exempting that family from release until playback moves off it.
  private sourceFamily?: string;
  private director?: MusicDirector;
  // Whether `start()` has been asked to begin the playlist. Set before init may have
  // resolved (the gesture that starts music can beat the async synth load), so init's
  // tail begins playback once ready. Idempotent guard against a reconnect's re-`start`.
  private started = false;
  // Whether the synth graph is fully built and ready to sequence.
  private ready = false;
  // The single in-flight/settled init, so repeated `init()` calls share one build.
  private initPromise?: Promise<void>;
  // Music mute + level, independent of the SFX master's, persisted by `src/music.ts`.
  // Stored separately from the graph so a choice made before init still applies.
  private muted = false;
  private level = 1;
  // Whether the local player is in a live duel (ADR-0146, duel-combat override), driven
  // by `main.ts` off the player's own replicated combat stance. Remembered here so a set
  // before `start()` builds the director still takes effect once it does.
  private combat = false;
  // Whether a Blackthorn's Storm is running (PRD #289 storm override), driven by `main.ts`
  // off the replicated StormStatus active edge. Remembered like `combat` so a set before the
  // director exists still applies once it does.
  private storm = false;
  // Whether the local player's Arena run is live (Arena Slice E, ADR-0177), driven by
  // `main.ts` off the owner-only ArenaRunStatus active edge. Remembered like the others.
  private arena = false;
  // The arena intensity tier main.ts last pushed (arenaMusicTier over elapsedTicks),
  // remembered like the flags so a set before the director exists still applies.
  private arenaTier = 1;
  // Whether a one-shot victory sting (ADR-0210) is playing: on its `songEnded` we revert
  // to the director's resolved track instead of advancing a playlist. Any explicit
  // (re)load via playResolved clears it, so a mid-sting override toggle can't double-revert.
  private stingActive = false;

  /**
   * Build the music graph on the shared context: the dedicated gain node, then (best
   * effort) the soundfont, worklet, synth, and sequencer plus the baked tracks.
   * Idempotent - later calls return the same promise. Any failure (no soundfont,
   * worklet unsupported, decode error) leaves the player a silent no-op. Safe to call
   * up front, in parallel with the rest of asset loading.
   */
  init(ctx: AudioContext): Promise<void> {
    this.initPromise ??= this.buildGraph(ctx);
    return this.initPromise;
  }

  private async buildGraph(ctx: AudioContext): Promise<void> {
    this.ctx = ctx;
    // The dedicated music gain, wired before anything async so a mute/volume set while
    // the synth is still loading already takes effect on the (silent) node.
    const gain = ctx.createGain();
    gain.gain.value = this.musicGain();
    gain.connect(ctx.destination);
    this.gain = gain;

    try {
      // Only the soundfont + the small `.mid` set are loaded up front. The ~85MB of Arena
      // intensity-remix mp3s are deliberately NOT fetched or decoded here - they are the
      // single biggest asset in the game and are needed only inside an Arena run, so they
      // stream in lazily on arena entry (setArena -> ensureArenaRemixes). A player who
      // never enters the arena never pays for them (and the login critical path is freed).
      const [soundfont, tracks] = await Promise.all([loadSoundfont(), loadMusicTracks()]);
      // No soundfont -> nothing can be synthesized; stay a silent no-op.
      if (!soundfont) return;
      await ctx.audioWorklet.addModule(processorUrl);
      const synth = new WorkletSynthesizer(ctx);
      await synth.soundBankManager.addSoundBank(soundfont, 'main');
      await synth.isReady;
      synth.connect(gain);
      this.synth = synth;
      for (const t of tracks) this.tracks.set(t.id, t.binary);

      // Play each track from the top: `skipToFirstNoteOn` (default) would fast-forward
      // past leading silence, which for background music would clip a track's intro -
      // and, worse, collapses a note-less silent stub `.mid` to zero duration so it
      // never ends (stalling the playlist). A music player wants the whole track.
      const seq = new Sequencer(synth, { skipToFirstNoteOn: false });
      // We drive advancement + loop-back ourselves via the pure playlist, so disable
      // the sequencer's own per-song looping (its default loops the current song).
      seq.loopCount = 0;
      seq.eventHandler.addEvent('songEnded', 'britbank-music-advance', () => this.onSongEnded());
      this.seq = seq;
      this.ready = true;
      // If start() already fired (its gesture raced ahead of this async build), begin now.
      // Sync the director to the latest combat flag first: a duel that began during the
      // async build set `this.combat` but couldn't reach the not-yet-ready director.
      if (this.started) {
        this.director?.setArena(this.arena);
        this.director?.setStorm(this.storm);
        this.director?.setCombat(this.combat);
        this.director?.setArenaTier(this.arenaTier); // after setArena (engaging resets the tier)
        this.playResolved(this.director?.resolve());
      }
    } catch (err) {
      console.warn('music player init failed - music disabled:', err);
    }
  }

  /**
   * Begin the login playlist from the top. Called on entering the world (a user
   * gesture, so the shared context is already resumed). Idempotent: a second call - a
   * reconnect's fresh `Welcome` - is a no-op, so the music keeps playing uninterrupted
   * and players never stack. If init hasn't finished, playback begins from its tail.
   */
  start(): void {
    if (this.started) return;
    this.started = true;
    // The director skips/degrades on our loaded maps: an unbaked login entry is
    // skipped, and the duel-combat override falls back to the playlist when combat1
    // isn't baked (graceful absence). An id is available whether it is a mid or a
    // decoded remix - the director picks, this shell routes by which map holds it.
    // The closures read the live maps, so this is correct even if start() races
    // ahead of the async track load.
    this.director = new MusicDirector(LOGIN_PLAYLIST, {
      isAvailable: (id) => this.tracks.has(id) || this.remixes.has(id),
      arenaTracks: ARENA_PLAYLIST, // the pool a run picks its one song from (ADR-0232)
    });
    // A run/storm/duel that began before entry (a set before start) still applies.
    if (this.arena) this.director.setArena(true);
    if (this.storm) this.director.setStorm(true);
    if (this.combat) this.director.setCombat(true);
    this.director.setArenaTier(this.arenaTier); // after setArena (engaging resets the tier)
    if (this.ready) this.playResolved(this.director.resolve());
  }

  /**
   * Switch the local player's music into or out of the looping duel-combat track
   * (ADR-0146). Driven by `main.ts` off the player's own replicated combat stance, so
   * both duelists switch independently and every other client is untouched - there is no
   * networked music state. Every duel end path (win/loss/draw/decline/timeout/disconnect/
   * storm-cancel) reduces to the stance dropping, i.e. `setCombat(false)`.
   *
   * Idempotent (the director no-ops a repeated state) and leak-free: it only ever
   * (re)loads the single sequencer's one song, never stacking players. Safe before
   * {@link start} (the flag is remembered) and before {@link init} (a no-op until ready).
   * When combat1 isn't baked the switch degrades to leaving the playlist playing.
   */
  setCombat(active: boolean): void {
    this.combat = active;
    const dir = this.director;
    if (!dir || !this.ready) return;
    // Reload only when the override actually changes what plays (director's own guard):
    // idempotent toggles and the unbaked-combat degrade both return false here.
    if (dir.setCombat(active)) this.playResolved(dir.resolve());
  }

  /**
   * Switch the background music into or out of the looping Blackthorn's Storm track
   * (PRD #289 storm fix), the twin of {@link setCombat}. Driven by `main.ts` off the
   * replicated StormStatus active edge, so every client in the bank swaps for the event's
   * duration and reverts (to whatever the login playlist / a duel wants) when it ends.
   *
   * Idempotent and leak-free (the director no-ops a repeated state and only ever reloads the
   * single song), and safe before {@link start}/{@link init} (the flag is remembered). When
   * `dungeon2` isn't baked it degrades to leaving the current music playing.
   */
  setStorm(active: boolean): void {
    this.storm = active;
    const dir = this.director;
    if (!dir || !this.ready) return;
    if (dir.setStorm(active)) this.playResolved(dir.resolve());
  }

  /**
   * Switch the local player's music into or out of the looping **Arena run** track
   * (Arena Slice E, PRD #334 / ADR-0177), the third sibling of {@link setCombat}/
   * {@link setStorm} and the top of the precedence order. Driven by `main.ts` off the
   * run-active edge of the owner-only `ArenaRunStatus` pushes, so only the runner's own
   * client swaps - a run is private, there is no networked music state. Every run end
   * path (death, survival, disconnect-forfeit at the next Welcome) reduces to
   * `setArena(false)`.
   *
   * Idempotent and leak-free (the director no-ops a repeated state and only ever reloads
   * the single song), and safe before {@link start}/{@link init} (the flag is
   * remembered). When the arena track isn't baked it degrades to leaving the current
   * music playing - never a silent gap.
   */
  setArena(active: boolean): void {
    this.arena = active;
    const dir = this.director;
    if (!dir || !this.ready) return;
    // Pick the run's one song at random (ADR-0232 - the whole run then sticks to
    // it): the pure director stays deterministic, so the randomness lives here in
    // the shell. Reseed before the engage decision so the first resolved track is
    // the chosen song's mid.
    if (active) dir.reseedArena(Math.floor(Math.random() * ARENA_PLAYLIST.length));
    if (active) this.arenaTier = 1; // every run opens on the base mid; the ramp is per-run
    // Now that a run is actually starting, stream in ONLY this run song's intensity
    // remixes (the mp3 lane, deferred out of buildGraph for perf). Fire-and-forget: the
    // base mid opens the run and the director degrades to it until a remix is decoded, so
    // there is never a gap - by the time the ramp reaches tier 2 (a track boundary ~100s
    // in) the load has long finished. whenArenaRemixesReady() exposes it for tests.
    if (active) this.arenaRemixLoad = this.ensureArenaRemixes(dir.arenaTrack);
    if (dir.setArena(active)) this.playResolved(dir.resolve());
  }

  /**
   * Resolves once the current run song's intensity remixes are fetched + decoded (or
   * immediately when no run is active). `main.ts` fires {@link setArena} and never awaits
   * this - the base mid plays while the remixes stream in and the ramp only reaches a
   * remix at a track boundary well after the load settles. Tests await it to drive the
   * ramp deterministically.
   */
  whenArenaRemixesReady(): Promise<void> {
    return this.arenaRemixLoad ?? Promise.resolve();
  }

  /**
   * Fetch + decode the intensity remixes (`_2..ARENA_INTENSITY_TIERS`) for one arena run
   * song, once. Cached per base song (a re-entered run reuses the settled load), and a
   * no-op without a decoded song id or an audio context. This is the deferred mp3-lane
   * load that keeps the ~85MB of remixes off every non-arena session and the login path.
   */
  private ensureArenaRemixes(base: string | undefined): Promise<void> {
    const ctx = this.ctx;
    if (!base || !ctx) return Promise.resolve();
    // This run's family becomes the most recently used; anything now beyond the
    // active + warm-set limit is released (deferred while its remix still sounds).
    this.remixFamilies.touch(base);
    this.pruneRemixFamilies();
    let load = this.remixLoads.get(base);
    if (!load) {
      // The load carries an identity check against the registered promise (not a
      // mere has(base)): if the family is released and immediately re-entered, the
      // re-entry registers a NEW load, and the stale one must recognise it is no
      // longer current and write nothing (issue #626 review).
      load = this.loadArenaRemixes(base, ctx, () => this.remixLoads.get(base) === load);
      this.remixLoads.set(base, load);
    }
    return load;
  }

  /**
   * Release every remix family beyond the active + warm-set limit (issue #626),
   * **except** the one the live source is still playing - that family is released
   * at the next prune after playback moves off it (a swap, stop, or natural end
   * all re-prune). Releasing drops the decoded buffers and the settled per-song
   * load, so a much later re-entry fetches the family afresh.
   */
  private pruneRemixFamilies(): void {
    for (const base of this.remixFamilies.excess(this.sourceFamily)) {
      this.remixFamilies.drop(base);
      this.remixLoads.delete(base);
      for (let tier = 2; tier <= ARENA_INTENSITY_TIERS; tier++) {
        this.remixes.delete(arenaRemixTrackId(base, tier));
      }
    }
  }

  /** The remix families currently decoded (tests/metrics), derived from the buffers. */
  residentRemixFamilies(): string[] {
    const bases = new Set<string>();
    for (const id of this.remixes.keys()) bases.add(remixFamilyOf(id));
    return [...bases];
  }

  /** Fetch this song's remix `.mp3`s and decode each into the {@link remixes} map. A
   * remix that fails to fetch/decode is skipped (the director degrades that tier down to
   * the base mid), so a partial load never silences the run. `isCurrent` reports whether
   * this load is still the family's registered one (a promise-identity check from
   * {@link ensureArenaRemixes}); a stale load - the family was released, possibly
   * released-and-re-entered - writes nothing. */
  private async loadArenaRemixes(
    base: string,
    ctx: AudioContext,
    isCurrent: () => boolean,
  ): Promise<void> {
    const ids: string[] = [];
    for (let tier = 2; tier <= ARENA_INTENSITY_TIERS; tier++) ids.push(arenaRemixTrackId(base, tier));
    const remixes = await loadMusicRemixes(ids);
    for (const r of remixes) {
      if (this.remixes.has(r.id)) continue;
      // The family may have been released (or released and re-entered under a NEW
      // load) while this one was in flight (a rapid run-song churn); a stale load
      // must not resurrect buffers behind the residency's back.
      if (!isCurrent()) return;
      try {
        this.remixes.set(r.id, await ctx.decodeAudioData(r.binary));
      } catch (err) {
        console.warn(`failed to decode arena remix "${r.id}" - skipped:`, err);
      }
    }
  }

  /**
   * Record the arena **intensity tier** (1 = the base `.mid`, up to the `_5` remix) -
   * driven by `main.ts` off `arenaMusicTier(elapsedTicks)` on every active
   * `ArenaRunStatus` push. Deliberately triggers no reload: the director consults the
   * tier when the *next* track loads (the at-track-end semantics), so the ramp is heard
   * at song boundaries, never as a mid-song cut. Safe before {@link start}/{@link init}
   * (the tier is remembered and forwarded once the director exists).
   */
  setArenaTier(tier: number): void {
    this.arenaTier = tier;
    this.director?.setArenaTier(tier);
  }

  /**
   * Play the one-shot **victory sting** (ADR-0210) - the new-Arena-high-score
   * fanfare - once, then revert to whatever the director resolves (login playlist,
   * or a duel/storm/arena override still engaged). Driven by `main.ts` off the
   * broadcast `ArenaRecord` message, so every client in earshot celebrates. When
   * `victory` isn't baked it degrades to a quiet no-op (the graceful path); safe
   * before {@link start}/{@link init} (a no-op until ready).
   */
  playVictory(): void {
    const seq = this.seq;
    if (!seq || !this.ready) return;
    const binary = this.tracks.get(VICTORY_TRACK_ID);
    if (!binary) return; // not baked → leave the current music playing
    this.stingActive = true;
    this.stopSource(); // the sting takes the mix from a playing remix too
    void this.ctx?.resume();
    seq.loadNewSongList([{ binary: binary.slice(0), fileName: `${VICTORY_TRACK_ID}.mid` }]);
    seq.play();
    this.fadeIn();
  }

  /**
   * Load and play a resolved track id (from the director), fading the incoming track in
   * from silence (see {@link FADE_SECONDS}). Routes by lane: a decoded Arena remix id
   * plays as an AudioBuffer source (pausing the sequencer), any other id plays its
   * `.mid` through the synth (stopping any live source). A missing/undefined id is a
   * quiet no-op, so the current playback simply continues - the graceful path when the
   * requested track isn't baked. The MIDI bytes are copied per play (`slice(0)`) so a
   * track survives the synth transferring its buffer, letting the loop replay it later.
   */
  private playResolved(id: string | undefined): void {
    if (!id) return;
    const remix = this.remixes.get(id);
    if (remix) {
      this.playRemix(remix, id);
      return;
    }
    const seq = this.seq;
    const binary = seq ? this.tracks.get(id) : undefined;
    if (!seq || !binary) return;
    // Any explicit (re)load ends the sting's revert-on-end contract (ADR-0210).
    this.stingActive = false;
    this.stopSource(); // the synth lane takes the mix back from a playing remix
    void this.ctx?.resume();
    seq.loadNewSongList([{ binary: binary.slice(0), fileName: `${id}.mid` }]);
    seq.play();
    this.fadeIn();
  }

  /**
   * Play one decoded Arena remix through the mp3 lane: a fresh one-shot
   * {@link AudioBufferSourceNode} into the shared music gain (so volume/mute/fade apply
   * identically), silencing the sequencer underneath. Its natural end drives the same
   * {@link onSongEnded} advance as the synth's `songEnded`; a source replaced by a
   * later track swap has its handler detached first, so a swap never double-advances.
   */
  private playRemix(buffer: AudioBuffer, id: string): void {
    const ctx = this.ctx;
    const gain = this.gain;
    if (!ctx || !gain) return;
    this.stingActive = false;
    // The incoming family is handed to stopSource so the prune it triggers can
    // never - even in principle - release the family about to play (issue #626
    // review): sourceFamily is set to the new family *before* that prune runs.
    this.stopSource(remixFamilyOf(id));
    try {
      this.seq?.pause();
    } catch {
      /* nothing loaded yet */
    }
    void ctx.resume();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(gain);
    source.onended = () => {
      if (this.source !== source) return; // superseded by a later swap
      this.source = undefined;
      // Playback moved off the family: any deferred release is safe now.
      this.sourceFamily = undefined;
      this.pruneRemixFamilies();
      source.disconnect();
      this.onSongEnded();
    };
    this.source = source;
    source.start();
    this.fadeIn();
  }

  /**
   * Stop and detach the live remix source, if any, without advancing the playlist.
   * `nextFamily` is the remix family about to take the mix (set when the caller is
   * {@link playRemix}), recorded **before** the reclaim prune runs so the prune can
   * never release the family that is about to play; the synth-lane callers omit it,
   * clearing the family so any deferred release is reclaimed.
   */
  private stopSource(nextFamily?: string): void {
    // Record the successor family first: the prune below must already see it.
    this.sourceFamily = nextFamily;
    const source = this.source;
    if (!source) {
      if (nextFamily === undefined) return;
      this.pruneRemixFamilies();
      return;
    }
    this.source = undefined;
    // The stopped source no longer references its family: reclaim any deferred
    // release before the next lane takes the mix (protecting nextFamily, above).
    this.pruneRemixFamilies();
    source.onended = null;
    try {
      source.stop();
    } catch {
      /* already stopped */
    }
    try {
      source.disconnect();
    } catch {
      /* already disconnected */
    }
  }

  /**
   * A track finished. A finished **victory sting** reverts to the director's current
   * resolution (ADR-0210) rather than advancing a playlist; otherwise the director
   * loops the override / advances the playlist as usual.
   */
  private onSongEnded(): void {
    const dir = this.director;
    if (!dir) return;
    if (this.stingActive) {
      this.stingActive = false;
      this.playResolved(dir.resolve());
      return;
    }
    this.playResolved(dir.songEnded());
  }

  /**
   * Mute or unmute the music, driven by the settings panel's music mute button. Pins
   * the music gain to 0 (hard silence) while the sequencer keeps running, so unmuting
   * resumes exactly where the music is. The {@link level} is untouched. Safe any time,
   * including before {@link init} builds the gain (the flag is remembered).
   */
  setMuted(muted: boolean): void {
    this.muted = muted;
    this.applyGain();
  }

  /**
   * Set the music volume level (`[0, 1]`, clamped), driven by the settings panel's music
   * slider. Rides within {@link MUSIC_CEILING}, independent of {@link muted}. Applies
   * live and is remembered if set before {@link init}. Safe any time.
   */
  setVolume(level: number): void {
    this.level = clamp01(level);
    this.applyGain();
  }

  /**
   * Push the current audible gain onto the node, if it exists yet. Cancels any in-flight
   * fade first and pins the value, so a slider drag or mute mid-transition takes effect
   * immediately and always wins over the fade - keeping the music mix under the slider/
   * mute at all times (ADR-0105/0146), independent of the SFX volume.
   */
  private applyGain(): void {
    const g = this.gain;
    if (!g) return;
    const now = this.ctx?.currentTime ?? 0;
    g.gain.cancelScheduledValues(now);
    g.gain.setValueAtTime(this.musicGain(), now);
  }

  /**
   * Ramp the music gain from silence up to the current {@link musicGain} over
   * {@link FADE_SECONDS}, fading the just-loaded track in. Cancels any prior ramp first
   * so rapid track swaps (rapid duel toggles) are last-write-wins with no stacking. When
   * muted the target is 0, so a fade during mute stays silent (mute is honoured); a mute/
   * slider change mid-fade cancels it via {@link applyGain}.
   */
  private fadeIn(): void {
    const g = this.gain;
    if (!g) return;
    const now = this.ctx?.currentTime ?? 0;
    g.gain.cancelScheduledValues(now);
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(this.musicGain(), now + FADE_SECONDS);
  }

  /** The audible music gain: silence when muted, else the level within the ceiling. */
  private musicGain(): number {
    return this.muted ? 0 : this.level * MUSIC_CEILING;
  }

  /**
   * Tear down the synth, sequencer, and gain node and stop the playlist. Used on page
   * unload; a normal reconnect never calls this (the idempotent {@link start} keeps the
   * one player running), so this is purely a clean-shutdown / no-leak path.
   */
  dispose(): void {
    this.stopSource();
    try {
      this.seq?.pause();
    } catch {
      /* sequencer already gone */
    }
    try {
      this.synth?.destroy();
    } catch {
      /* synth already gone */
    }
    try {
      this.gain?.disconnect();
    } catch {
      /* node already disconnected */
    }
    this.seq = undefined;
    this.synth = undefined;
    this.gain = undefined;
    this.director = undefined;
    this.tracks.clear();
    this.remixes.clear();
    this.remixLoads.clear();
    this.remixFamilies.clear();
    this.sourceFamily = undefined;
    this.arenaRemixLoad = undefined;
    this.started = false;
    this.ready = false;
    this.combat = false;
    this.storm = false;
    this.arena = false;
    this.arenaTier = 1;
    this.stingActive = false;
    this.initPromise = undefined;
  }
}
